import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { categories } from '@/mocks/categories';

interface CategoryDropdownProps {
  align?: 'left' | 'right';
}

export default function CategoryDropdown({ align = 'left' }: CategoryDropdownProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={ref} className="relative w-full">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-2 rounded-md border border-background-200 bg-background-50 px-4 py-3 text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="flex items-center gap-2">
          <i className="ri-apps-2-line text-foreground-500" />
          {t('category.dropdownLabel')}
        </span>
        <i className={`ri-arrow-down-s-line text-foreground-500 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <ul
          className={`absolute z-50 mt-2 max-h-80 w-full overflow-y-auto rounded-lg border border-background-200 bg-background-50 py-1 ${
            align === 'right' ? 'right-0' : 'left-0'
          }`}
          role="listbox"
        >
          {categories.map((cat) => (
            <li key={cat.id}>
              <a
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
                onClick={() => setOpen(false)}
              >
                <span className="flex h-7 w-7 items-center justify-center rounded-md bg-secondary-50 text-secondary-700">
                  <i className={`${cat.icon} text-sm`} />
                </span>
                <span>{t(cat.nameKey)}</span>
                <i className="ri-arrow-right-up-line ml-auto text-foreground-400 text-sm" />
              </a>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}